import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { ApprovalDecision } from '@/types/domain';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
}

export function ApproveModal({ ticketId, open, onClose }: Props) {
  const [decision, setDecision] = useState<ApprovalDecision>(ApprovalDecision.Approve);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await ticketsApi.approve(ticketId, { decision, notes: notes || undefined });
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: () => {
      push({ kind: 'success', title: `Approval ${decision}` });
      // V4.9.3 — comprehensive invalidation: detail, lists, dashboard,
      // approvals queue, archives, deleted.
      invalidateAfterTicketAction(queryClient, ticketId);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Approval failed');
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Decide on approval"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${decision === ApprovalDecision.Deny ? 'btn-danger' : 'btn-primary'}`}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : `Submit ${decision}`}
          </button>
        </>
      }
    >
      <Field label="Decision" required>
        <select
          className="form-select"
          value={decision}
          onChange={(e) => setDecision(e.target.value as ApprovalDecision)}
        >
          <option value={ApprovalDecision.Approve}>Approve</option>
          <option value={ApprovalDecision.Deny}>Deny</option>
          <option value={ApprovalDecision.Info}>Request more info</option>
        </select>
      </Field>
      <Field label="Notes (optional)">
        <textarea
          className="form-textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </Field>
      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}
